import React, { useEffect, useState } from 'react';
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Button,
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
} from 'reactstrap';

import { Attivita, Utenti } from '../../../Types/Panel.type';
import UserAutocomplete, { UtenteSearch } from '../../../Components/Autocomplete/UserAutocomplete';
import { postForm, get, del } from '../../../hook/request';
import DeleteConfirm from '../../../Components/Modals/Delete/DopdownDelete';

type CategoryOption = { id: number; label: string };

type DescrizioneEditorProps = {
  value: Attivita;
  statoOptions: CategoryOption[];
  tipoOptions: CategoryOption[];
  onSave: (next: Attivita) => void | Promise<Attivita | void>;
  title?: string;
  triggerClassName?: string;
  onOpenTask?: (attivita: Attivita) => void;
  isOpen?: boolean;
  onClose?: ()=> void;
  onOpen?: () => void;
};

type AttivitaFile = {
  ID: number;
  attivitaID: number;
  file_url: string;
  file_name: string;
};

const DescrizioneEditor: React.FC<DescrizioneEditorProps> = ({
  value,
  statoOptions,
  tipoOptions,
  onSave,
  title = 'Modifica attività',
  triggerClassName = 'btn btn-sm btn-outline-secondary',
  onOpenTask,
  isOpen,
  onClose,
  onOpen,
}) => {

  // const [open, setOpen] = useStatDescrizioneEditor(false);
  const [localRow, setLocalRow] = useState<Attivita>({
    ...value,
    utenti: value.utenti || [],
  });
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isOpen ?? internalOpen;




  const toggle = () => {
    if (saving) return;
    if(isOpen !== undefined){
      onClose?.();
    }else{
      setInternalOpen(value => !value)
    }
  };



  const handleChange = <K extends keyof Attivita>(field: K, val: Attivita[K]) => {
    setLocalRow(prev => ({ ...prev, [field]: val }));
  };

  const handleAddUser = (u: UtenteSearch) => {
    setLocalRow(prev => {
      const already = prev.utenti?.some(x => String(x.ID) === String(u.ID));
      if (already) return prev;

      const nuovo: Utenti = {
        ID: String(u.ID),
        nominativo: `${u.nome} ${u.cognome}`.trim(),
        email: u.email ?? '',
      };

      return {
        ...prev,
        utenti: [...(prev.utenti || []), nuovo],
      };
    });
  };

  const handleRemoveUser = (id: string) => {
    setLocalRow(prev => ({
      ...prev,
      utenti: (prev.utenti || []).filter(u => String(u.ID) !== String(id)),
    }));
  };


  const [files, setFiles] = useState<AttivitaFile[]>([]);
  const [filesAccordionOpen, setFilesAccordionOpen] = useState<string | string[]>('');
  const toggleFilesAccordion = (id: string) => {
    setFilesAccordionOpen(prev => (prev === id ? '' : id));
  };

  const loadFiles = async (attivitaId: number) => {
    try {
      const res = await get<AttivitaFile[] | { data?: AttivitaFile[] }>(
        `/progetti/attivita_file?ID=${attivitaId}`
      );
      const payload: any = (res as any)?.data ?? res;
      const list: AttivitaFile[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      setFiles(list);
    } catch (err) {
      console.error('Errore caricando i file attività:', err);
    }
  };

  useEffect(() => {
    if (open) {
      setLocalRow({
        ...value,
        utenti: value.utenti || [],
      });
      setSelectedFile(null);
      if (value.ID) {
        loadFiles(value.ID);
      }
    }
  }, [open]);


  const [fileInputKey, setFileInputKey] = useState(0);

  const handleConfirm = async () => {
    try {
      setSaving(true);
      const res = await onSave(localRow);
      const saved = (res as Attivita) || localRow;
      const attivitaId = saved.ID || localRow.ID;
      if (selectedFile && attivitaId) {
        const formData = new FormData();
        formData.append('file_url', selectedFile);
        formData.append('file_name', localRow.nome_file || selectedFile.name);
        formData.append('attivitaID', String(attivitaId));
        await postForm(`/progetti/attivita_file?ID=${attivitaId}`, formData);
        setSelectedFile(null);
        setFileInputKey(prev => prev + 1); 
        setLocalRow(prev => ({
          ...prev,
          nome_file: '',
        }));
        await loadFiles(attivitaId);
      }

      if (saved.ID) {
        setLocalRow(prev => ({
          ...prev,
          ...saved,
          nome_file: '',   
          _isNew: false,
        }));
      }
    } finally {
      setSaving(false);
    }
  };






  return (
    <>
      <button
        type="button"
        className={triggerClassName}
        onClick={() => {
          if (isOpen !== undefined) onOpen?.();
          else setInternalOpen(true);
        }}
        title="Modifica attività"
      >
        <i className="fa-regular fa-pen-to-square" />
      </button>

      <Offcanvas
        style={{ width: 500 }}
        isOpen={open}
        toggle={toggle}
        direction="start"
      >
        <OffcanvasHeader toggle={toggle}>{title}</OffcanvasHeader>
        <OffcanvasBody>
          {/* Descrizione */}
          <div className="mb-3">
            <label className="form-label">Descrizione</label>
            <textarea
              className="form-control"
              rows={4}
              value={localRow.descrizione ?? ''}
              onChange={e => handleChange('descrizione', e.target.value)}
            />
          </div>

          {/* Stato */}
          <div className="mb-3">
            <label className="form-label">Stato</label>
            <select
              className="form-select"
              value={localRow.stato ?? ''}
              onChange={e => handleChange('stato', e.target.value)}
            >
              <option value="">-- Seleziona stato --</option>
              {statoOptions.map(opt => (
                <option key={opt.id} value={String(opt.id)}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Tipo */}
          <div className="mb-3">
            <label className="form-label">Tipo</label>
            <select
              className="form-select"
              value={localRow.tipo ?? ''}
              onChange={e => handleChange('tipo', e.target.value)}
            >
              <option value="">-- Seleziona tipo --</option>
              {tipoOptions.map(opt => (
                <option key={opt.id} value={String(opt.id)}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Utenti */}
          <div className="mb-3">
            <label className="form-label">Utenti</label>
            <UserAutocomplete
              selectedUsers={localRow.utenti || []}
              onAddUser={handleAddUser}
              onRemoveUser={handleRemoveUser}
            />
          </div>

          {/* Priorità */}
          <div className="mb-3">
            <label className="form-label">Priorità</label>
            <select
              className="form-select form-select-sm"
              value={localRow.priorita ?? 1}
              onChange={e =>
                handleChange('priorita', Number(e.target.value))
              }
            >
              <option value={3}>🔴 Alto</option>
              <option value={2}>🟡 Medio</option>
              <option value={1}>🟢 Basso</option>
            </select>
          </div>

          {/* Tempo esecuzione */}
          <div className="mb-3">
            <label className="form-label">Tempo esecuzione</label>
              <input
                type="text"
                className="form-control"
                placeholder="HH:MM (es. 48:30)"
                value={localRow.tempo_esecuzione ?? ''}
                onChange={e => {
                  const value = e.target.value;
                  if (/^\d{0,5}(:[0-5]?\d?)?$/.test(value)) {
                    handleChange('tempo_esecuzione', value);
                  }
                }}
              />
          </div>

          {/* Data da svolgere */}
          <div className="mb-3">
            <label className="form-label">Data da svolgere</label>
            <input
              type="date"
              className="form-control"
              value={localRow.data_svolgere?.slice(0, 10) ?? ''}
              onChange={e => handleChange('data_svolgere', e.target.value)}
            />
          </div>

          {/* Data esecuzione */}
          <div className="mb-3">
            <label className="form-label">Data esecuzione</label>
            <input
              type="date"
              className="form-control"
              value={localRow.data_esecuzione?.slice(0, 10) ?? ''}
              onChange={e => handleChange('data_esecuzione', e.target.value)}
            />
          </div>


          {/* Rapportino */}
          <div className="mb-3 form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="rapportinoCheck"
              checked={!!localRow.rapportino}
              onChange={e => handleChange('rapportino', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="rapportinoCheck">
              Rapportino
            </label>
          </div>


          <Accordion
            open={filesAccordionOpen}
            toggle={toggleFilesAccordion}
            className="dark-accordion my-3"
          >
            <AccordionItem className="accordion-wrapper accordion-left-border">
              <AccordionHeader
                targetId="1"
                className="text-info"
              >
                <i className="fa-solid fa-folder me-2"></i>
                File allegati
                {filesAccordionOpen === '1' ? (
                  <i className="iconly-Arrow-Up-2 icli ms-auto icon font-primary" />
                ) : (
                  <i className="iconly-Arrow-Down-2 icli ms-auto icon font-primary" />
                )}
              </AccordionHeader>

              <AccordionBody accordionId="1">
                {/* Input nome file */}
                <div className="mb-3">
                  <label className="form-label">Nome file</label>
                  <input
                    type="text"
                    className="form-control"
                    value={localRow.nome_file ?? ''}
                    onChange={e => handleChange('nome_file', e.target.value as any)}
                    placeholder="Es. documento.pdf"
                  />
                </div>

                {/* Input upload file */}
                <div className="mb-3">
                  <label className="form-label">Allega file</label>
                  <input
                      key={fileInputKey}
                      type="file"
                      className="form-control"
                      onChange={e => {
                        const file = e.target.files?.[0] ?? null;
                        setSelectedFile(file);
                        if (file && !localRow.nome_file) {
                          setLocalRow(prev => ({ ...prev, nome_file: file.name }));
                        }
                      }}
                    />
                  {selectedFile && (
                    <small className="text-muted d-block mt-1">
                      File selezionato: <strong>{selectedFile.name}</strong>
                    </small>
                  )}
                </div>

                {/* Lista file già presenti */}
                {files.length > 0 && (
                  <div className="mt-3">
                    <h6>File presenti:</h6>
                    <ul className="list-unstyled mb-2">
                      {files.map((f, idx) => (
                        <li key={`${f.attivitaID}-${idx}`} className="d-flex mt-2 align-items-center justify-content-between mb-1 w-100">
                          <div>
                            <i className="fa-regular fa-file me-2" />
                            <a
                              href={f.file_url.replace(/ /g, '_')}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {f.file_name}
                            </a>
                          </div>
                          <div>
                              <DeleteConfirm
                                url={`/progetti/attivita_file?ID=${f.ID}` }
                                onSuccess={ () => {
                                  if(localRow.ID){
                                    loadFiles(localRow.ID)}
                                  }}
                              />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {files.length === 0 && (
                  <small className="text-muted">Nessun file caricato.</small>
                )}
              </AccordionBody>
            </AccordionItem>
          </Accordion>


          {/* 👉 Bottone "Aggiungi task" visibile solo se c'è un ID */}
          {localRow.ID && onOpenTask && (
            <div className="mb-3 d-flex justify-content-end">
              <Button
                color="secondary"
                size="sm"
                onClick={() => onOpenTask(localRow)}
              >
                <i className="fa-solid fa-list-check me-1" />
                Aggiungi task
              </Button>
            </div>
          )}


          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button color="light" style={{ color: 'black' }} onClick={toggle}>
              Annulla
            </Button>
            <Button color="primary" onClick={handleConfirm} disabled={saving}>
              {saving ? 'Salvo...' : 'Salva'}
            </Button>
          </div>
        </OffcanvasBody>
      </Offcanvas>
    </>
  );
};

export default DescrizioneEditor;
